import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Code, Mail, Lock, User, Phone, GraduationCap, Building, BookOpen, ArrowRight, CheckCircle, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import Logo from '../components/landing/Logo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// LinkedIn-inspired color palette
const colors = {
  primary: '#0a66c2',
  primaryDark: '#004182',
  primaryLight: '#e8f4f8',
  success: '#057642',
  text: '#191919',
  textSecondary: '#666666',
  background: '#f3f2ef',
  cardBg: '#ffffff',
  border: '#e0e0e0'
};

const CodeArenaAuthPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTarget = (() => {
    const r = searchParams.get('redirect');
    if (!r) return '/codearena/dashboard';
    // Only allow same-origin paths to prevent open-redirect
    return r.startsWith('/') && !r.startsWith('//') ? r : '/codearena/dashboard';
  })();
  const [mode, setMode] = useState('login');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const [loginData, setLoginData] = useState({
    email: '',
    password: ''
  });
  
  const [registerData, setRegisterData] = useState({
    full_name: '',
    email: '',
    password: '',
    confirm_password: '',
    phone: '',
    year_of_study: '2nd',
    department: '',
    college: '',
    university: ''
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const res = await axios.post(`${API}/codearena/login`, {
        email: loginData.email,
        password: loginData.password
      });
      
      localStorage.setItem('codearena_token', res.data.access_token);
      localStorage.setItem('codearena_student', JSON.stringify(res.data.student));
      toast.success('Login successful!');
      navigate(redirectTarget);
    } catch (err) {
      const detail = err.response?.data?.detail;
      // Handle Pydantic validation errors (array format) or string errors
      if (Array.isArray(detail)) {
        toast.error(detail.map(e => e.msg || e.message || String(e)).join(', ') || 'Login failed');
      } else if (typeof detail === 'object' && detail !== null) {
        toast.error(detail.msg || detail.message || JSON.stringify(detail) || 'Login failed');
      } else {
        toast.error(detail || 'Login failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (registerData.password !== registerData.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (registerData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);
    
    try {
      const res = await axios.post(`${API}/codearena/register`, {
        full_name: registerData.full_name,
        email: registerData.email,
        password: registerData.password,
        phone: registerData.phone,
        year_of_study: registerData.year_of_study,
        department: registerData.department,
        college: registerData.college,
        university: registerData.university || registerData.college
      });
      
      localStorage.setItem('codearena_token', res.data.access_token);
      localStorage.setItem('codearena_student', JSON.stringify(res.data.student));
      toast.success('Registration successful!');
      navigate(redirectTarget);
    } catch (err) {
      const detail = err.response?.data?.detail;
      // Handle Pydantic validation errors (array format) or string errors
      if (Array.isArray(detail)) {
        toast.error(detail.map(e => e.msg || e.message || String(e)).join(', ') || 'Registration failed');
      } else if (typeof detail === 'object' && detail !== null) {
        toast.error(detail.msg || detail.message || JSON.stringify(detail) || 'Registration failed');
      } else {
        toast.error(detail || 'Registration failed');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f3f2ef] flex flex-col">
      {/* Compact Header */}
      <header className="bg-white border-b border-gray-200 py-4 px-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/codearena" className="flex items-center">
            <Logo subtitle="Arena" size="md" noLink />
          </Link>
          <Link 
            to="/codearena" 
            className="text-[#B91C1C] hover:text-[#7F1D1D] text-sm font-medium"
          >
            Back to Home
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          {/* Auth Card */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            {/* Card Header */}
            <div className="px-6 pt-6 pb-4 text-center border-b border-gray-100">
              <h1 className="text-2xl font-semibold text-[#191919]">
                {mode === 'login' ? 'Welcome Back' : 'Join EvalentX Arena'}
              </h1>
              <p className="text-[#666666] text-sm mt-1">
                {mode === 'login' 
                  ? 'Sign in to continue your coding journey' 
                  : 'Start building your developer profile today'}
              </p>
            </div>

            {/* Tab Switcher */}
            <div className="flex border-b border-gray-100">
              <button
                onClick={() => setMode('login')}
                className={`flex-1 py-3 text-center font-medium text-sm transition-colors relative ${
                  mode === 'login' 
                    ? 'text-[#0a66c2]' 
                    : 'text-[#666666] hover:text-[#191919]'
                }`}
                data-testid="login-tab"
              >
                Sign In
                {mode === 'login' && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#0a66c2]" />
                )}
              </button>
              <button
                onClick={() => setMode('register')}
                className={`flex-1 py-3 text-center font-medium text-sm transition-colors relative ${
                  mode === 'register' 
                    ? 'text-[#0a66c2]' 
                    : 'text-[#666666] hover:text-[#191919]'
                }`}
                data-testid="register-tab"
              >
                Create Account
                {mode === 'register' && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#0a66c2]" />
                )}
              </button>
            </div>

            <div className="p-6">
              {mode === 'login' ? (
                <form onSubmit={handleLogin} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-[#191919] mb-1.5">Email</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-[#666666]" size={18} />
                      <input
                        type="email"
                        value={loginData.email}
                        onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                        className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-300 rounded-lg text-[#191919] placeholder-[#666666] focus:outline-none focus:ring-2 focus:ring-[#0a66c2]/20 focus:border-[#0a66c2] transition-colors"
                        placeholder="your@email.com"
                        required
                        data-testid="login-email"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-[#191919] mb-1.5">Password</label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-[#666666]" size={18} />
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={loginData.password}
                        onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                        className="w-full pl-10 pr-10 py-2.5 bg-white border border-gray-300 rounded-lg text-[#191919] placeholder-[#666666] focus:outline-none focus:ring-2 focus:ring-[#0a66c2]/20 focus:border-[#0a66c2] transition-colors"
                        placeholder="Enter your password"
                        required
                        data-testid="login-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[#666666] hover:text-[#191919]"
                      >
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                    <div className="mt-2 text-right">
                      <Link
                        to="/forgot-password/student"
                        className="text-sm text-[#0a66c2] hover:text-[#004182] font-medium transition-colors"
                        data-testid="student-forgot-password-link"
                      >
                        Forgot password?
                      </Link>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-3 bg-[#0a66c2] text-white rounded-full font-semibold hover:bg-[#004182] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    data-testid="login-submit"
                  >
                    {loading ? 'Signing in...' : (
                      <>Sign In <ArrowRight size={18} /></>
                    )}
                  </button>
                </form>
              ) : (
                <form onSubmit={handleRegister} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-[#191919] mb-1.5">Full Name</label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 text-[#666666]" size={18} />
                      <input
                        type="text"
                        value={registerData.full_name}
                        onChange={(e) => setRegisterData({ ...registerData, full_name: e.target.value })}
                        className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-300 rounded-lg text-[#191919] placeholder-[#666666] focus:outline-none focus:ring-2 focus:ring-[#0a66c2]/20 focus:border-[#0a66c2] transition-colors"
                        placeholder="John Doe"
                        required
                        data-testid="register-name"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-[#191919] mb-1.5">Email</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-[#666666]" size={18} />
                      <input
                        type="email"
                        value={registerData.email}
                        onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                        className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-300 rounded-lg text-[#191919] placeholder-[#666666] focus:outline-none focus:ring-2 focus:ring-[#0a66c2]/20 focus:border-[#0a66c2] transition-colors"
                        placeholder="your@email.com"
                        required
                        data-testid="register-email"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-[#191919] mb-1.5">Password</label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-[#666666]" size={18} />
                        <input
                          type={showPassword ? 'text' : 'password'}
                          value={registerData.password}
                          onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                          className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-300 rounded-lg text-[#191919] placeholder-[#666666] focus:outline-none focus:ring-2 focus:ring-[#0a66c2]/20 focus:border-[#0a66c2] transition-colors"
                          placeholder="6+ chars"
                          required
                          data-testid="register-password"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-[#191919] mb-1.5">Confirm</label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-[#666666]" size={18} />
                        <input
                          type={showConfirmPassword ? 'text' : 'password'}
                          value={registerData.confirm_password}
                          onChange={(e) => setRegisterData({ ...registerData, confirm_password: e.target.value })}
                          className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-300 rounded-lg text-[#191919] placeholder-[#666666] focus:outline-none focus:ring-2 focus:ring-[#0a66c2]/20 focus:border-[#0a66c2] transition-colors"
                          placeholder="Repeat"
                          required
                          data-testid="register-confirm-password"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-[#191919] mb-1.5">Phone</label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-[#666666]" size={18} />
                        <input
                          type="tel"
                          value={registerData.phone}
                          onChange={(e) => setRegisterData({ ...registerData, phone: e.target.value })}
                          className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-300 rounded-lg text-[#191919] placeholder-[#666666] focus:outline-none focus:ring-2 focus:ring-[#0a66c2]/20 focus:border-[#0a66c2] transition-colors"
                          placeholder="9876543210"
                          required
                          data-testid="register-phone"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-[#191919] mb-1.5">Year</label>
                      <select
                        value={registerData.year_of_study}
                        onChange={(e) => setRegisterData({ ...registerData, year_of_study: e.target.value })}
                        className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-lg text-[#191919] focus:outline-none focus:ring-2 focus:ring-[#0a66c2]/20 focus:border-[#0a66c2] transition-colors"
                        required
                        data-testid="register-year"
                      >
                        <option value="1st">1st Year</option>
                        <option value="2nd">2nd Year</option>
                        <option value="3rd">3rd Year</option>
                        <option value="4th">4th Year</option>
                        <option value="PG">PG</option>
                        <option value="PhD">PhD</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#191919] mb-1.5">Department</label>
                    <div className="relative">
                      <BookOpen className="absolute left-3 top-1/2 -translate-y-1/2 text-[#666666]" size={18} />
                      <input
                        type="text"
                        value={registerData.department}
                        onChange={(e) => setRegisterData({ ...registerData, department: e.target.value })}
                        className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-300 rounded-lg text-[#191919] placeholder-[#666666] focus:outline-none focus:ring-2 focus:ring-[#0a66c2]/20 focus:border-[#0a66c2] transition-colors"
                        placeholder="Computer Science"
                        required
                        data-testid="register-department"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#191919] mb-1.5">College/University</label>
                    <div className="relative">
                      <Building className="absolute left-3 top-1/2 -translate-y-1/2 text-[#666666]" size={18} />
                      <input
                        type="text"
                        value={registerData.college}
                        onChange={(e) => setRegisterData({ ...registerData, college: e.target.value })}
                        className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-300 rounded-lg text-[#191919] placeholder-[#666666] focus:outline-none focus:ring-2 focus:ring-[#0a66c2]/20 focus:border-[#0a66c2] transition-colors"
                        placeholder="IIT Gandhinagar"
                        required
                        data-testid="register-college"
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-3 bg-[#0a66c2] text-white rounded-full font-semibold hover:bg-[#004182] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    data-testid="register-submit"
                  >
                    {loading ? 'Creating account...' : (
                      <>Create Account <CheckCircle size={18} /></>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>

          {/* Trust Indicators */}
          <div className="mt-6 text-center">
            <p className="text-[#666666] text-sm mb-3">
              Trusted by <span className="text-[#191919] font-semibold">50,000+</span> developers
            </p>
            <div className="flex items-center justify-center gap-4 text-xs text-[#666666]">
              <span className="flex items-center gap-1">
                <CheckCircle size={14} className="text-[#057642]" />
                Free to join
              </span>
              <span className="flex items-center gap-1">
                <CheckCircle size={14} className="text-[#057642]" />
                100+ companies hiring
              </span>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default CodeArenaAuthPage;
